import React from "react";
import { t } from "ttag";

import { ProductUnavailable } from "../../../common/ProductUnavailable";
import { Trans } from "../../../common/Trans";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IProduct, IUserConfigurableBundle } from "../../../models/catalogue";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { money } from "../../../utils/format";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { getProductVariant } from "../../selectors";
import { setGiftOptionValue, setGiftRootProduct } from "../reducers";
import { IGiftSelection } from "../reducers.interfaces";
import { GiftMainImage } from "./GiftMainImage";
import { GiftOptions } from "./GiftOptions";
import { GiftThumbnail } from "./GiftThumbnail";

const calculateTotalOptionCount = (bundle: IUserConfigurableBundle): number => {
    return bundle.suggested_range_products.reduce<number>((memo, product) => {
        return memo + product.children.length;
    }, bundle.suggested_range_products.length);
};

const buildThumbnails = (
    bundle: IUserConfigurableBundle,
    selectedRootProduct: IProduct | null,
    giftID: string,
    dispatch: ReturnType<typeof useAppDispatch>,
): React.ReactNode[] => {
    return bundle.suggested_range_products.map((p) => {
        return (
            <GiftThumbnail
                key={p.id as unknown as number}
                rootProduct={p}
                isSelected={
                    !!selectedRootProduct && selectedRootProduct.id === p.id
                }
                onClick={() =>
                    dispatch(
                        setGiftRootProduct({
                            giftID,
                            bundleID: bundle.id,
                            rootProduct: p,
                        }),
                    )
                }
            />
        );
    });
};

const buildPrice = (selectedVariant: IProduct | null): React.ReactNode => {
    if (!selectedVariant || !selectedVariant.availability.is_available_to_buy) {
        return <ProductUnavailable variant={selectedVariant} />;
    }
    const displayPrice = getProductPrice(selectedVariant.price, {
        priceType: PriceType.RETAIL,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    return (
        <>
            <h3>{selectedVariant.title}</h3>
            <p>
                <strong>{t`FREE`}</strong>
                &nbsp;
                <Trans
                    fmtString={t`Was <Price></Price>`}
                    data={{
                        Price: () => <span key="1">{money(displayPrice)}</span>,
                    }}
                />
            </p>
        </>
    );
};

const useGiftState = (giftID: string, bundle: IUserConfigurableBundle) => {
    if (bundle.suggested_range_products.length <= 0) {
        // TODO(i18n): Wrap error message in t tagged template: t`Can not configure gift with no suggested products`
        throw new Error("Can not configure gift with no suggested products");
    }

    const giftConfig = useAppSelector((rootState) => {
        const state = rootState.checkout;
        return state.gifts.gifts[giftID] || ({} as IGiftSelection);
    });

    const selectedRootProductID =
        giftConfig.selectedRootProduct || bundle.suggested_range_products[0].id;

    const selectedRootProduct =
        bundle.suggested_range_products.find((p) => {
            return p.id === selectedRootProductID;
        }) || null;

    const optionValues = giftConfig.optionValues || {};

    const selectedVariant = selectedRootProduct
        ? getProductVariant([selectedRootProduct], optionValues)
        : null;

    return {
        selectedRootProduct,
        selectedVariant,
        optionValues,
    };
};

export const ConfigureGift = (props: {
    bundle: IUserConfigurableBundle;
    giftID: string;
    giftIdx: number;
    qtyIdx: number;
}): React.ReactNode => {
    const dispatch = useAppDispatch();
    const { selectedRootProduct, selectedVariant, optionValues } = useGiftState(
        props.giftID,
        props.bundle,
    );

    useMountEffect(() => {
        // Set default root product on mount
        const defaultRoot = props.bundle.suggested_range_products[0];
        if (!defaultRoot) {
            return;
        }
        dispatch(
            setGiftRootProduct({
                giftID: props.giftID,
                bundleID: props.bundle.id,
                rootProduct: defaultRoot,
            }),
        );
    });

    const totalOptionCount = calculateTotalOptionCount(props.bundle);

    let giftCTA: React.ReactNode = null;
    let thumbnails: React.ReactNode = null;
    let options: React.ReactNode = null;

    if (totalOptionCount > 1) {
        giftCTA = <p>{t`Choose your gift:`}</p>;
        thumbnails = (
            <ul className="configure-gift__thumbnails">
                {buildThumbnails(
                    props.bundle,
                    selectedRootProduct,
                    props.giftID,
                    dispatch,
                )}
            </ul>
        );
        options = (
            <div className="configure-gift__option-selectors">
                <GiftOptions
                    giftID={props.giftID}
                    rootProduct={selectedRootProduct}
                    variant={null}
                    optionValues={optionValues}
                    onOptionChange={(
                        namespace,
                        code,
                        index,
                        totalNumValues,
                        value,
                    ) =>
                        dispatch(
                            setGiftOptionValue({
                                giftID: props.giftID,
                                namespace,
                                code,
                                index,
                                totalNumValues,
                                value,
                            }),
                        )
                    }
                />
            </div>
        );
    }

    // TODO(i18n): Use msgid with ngettext for proper plural handling and number formatting
    const sectionTitle = t`Gift ${props.giftIdx}`;

    return (
        <section className="configure-gift" aria-label={sectionTitle}>
            <header>
                <h2>{sectionTitle}</h2>
                {giftCTA}
            </header>
            <div className="configure-gift__content">
                <div className="configure-gift__left">
                    <GiftMainImage
                        rootProduct={selectedRootProduct}
                        variant={selectedVariant}
                    />
                </div>
                <div className="configure-gift__right">
                    {thumbnails}
                    <div className="configure-gift__variant-details">
                        {buildPrice(selectedVariant)}
                    </div>
                    {options}
                </div>
            </div>
        </section>
    );
};
